import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import {
  KeyValue,
  Metadata,
  SimpleTableRow,
  TableHeader,
} from "../../interfaces";
import usePagination from "../../hooks/usePagination";
import ResultPagination from "../ResultPagination";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import { CSVLink } from "react-csv";
import { fileNameFormat, reduceObj } from "../../common/services/Utilities";
import SearchIcon from "@mui/icons-material/Search";
import BasicTabs from "../BasicTabs";
import SearchPills from "../SearchPills";
import clsx from "clsx";
import TextFilterField from "../TextFilterField";
import { isEqual } from "lodash";

const PREFIX = "Glossary";

const classes = {
  button: `${PREFIX}-button`,
  icon: `${PREFIX}-icon`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.button}`]: {
    "height": 40,
    "fontSize": ".875rem",
    "fontWeight": 400,
    "lineHeight": 1.5,
    "width": "100%",
    "color": theme.palette.primary.dark,
    "border": `1px solid ${theme.palette.primary.light} `,
    "borderRadius": 4,
    "marginTop": 12,
    "backgroundColor": "white",
    "&.CSVLink": {
      "display": "block",
      "padding": "9px 16px",
      "&:focus, &:active, &:hover": {
        backgroundColor: theme.palette.primary.main,
        color: "white",
        textDecoration: "none",
      },
    },
    "&.filterTableButton": {
      "padding": "18px",
      "&.MuiButton-root.MuiButton-outlinedPrimary:hover, &:focus, &:active": {
        backgroundColor: theme.palette.primary.main,
        color: "white",
        textDecoration: "none",
      },
    },
  },

  [`& .${classes.icon}`]: {
    "textAlign": "center",
    "padding": 4,
    "border": `1px solid #1A4480`,
    "color": theme.palette.primary.main,
    "backgroundColor": "#ffffff",
    "minWidth": 24,
    "fontWeight": 700,
    "fontSize": 14,
    "minHeight": 24,
    "margin": "1rem, 0rem",
    "&.all": {
      minWidth: 90,
    },
    "&.checked": {
      color: "#ffffff",
      backgroundColor: theme.palette.primary.main,
    },
    "&.disabled": {
      color: "#666666",
      backgroundColor: "#ced6e3",
      borderColor: "#ced6e3",
    },
  },
}));

interface Headers {
  title: string;
  apiField: string;
  colSpan?: number;
  style?: {};
}

const VtoZ = "V* OR W* OR X* OR Y* OR Z";
const alphabet = [..."ABCDEFGHIJKLMNOPQRSTU".split(""), VtoZ];
const sites = [
  { shortName: "FIB", longName: "Find Insured Banks", id: 1 },
  { shortName: "BSC", longName: "Bank Structure Changes", id: 2 },
  { shortName: "R&C", longName: "Reports and Comparisons", id: 3 },
  { shortName: "ADS", longName: "Annual Data Summaries", id: 4 },
  { shortName: "SOD", longName: "Summary of Deposits", id: 5 },
  { shortName: "F&A", longName: "Failures and Assistance", id: 6 },
];
const siteShortNames = ["FIB", "BSC", "R&C", "ADS", "SOD", "F&A"];

interface Props {
  appDisplay?: string;
  tableCheckBoxes?: KeyValue;
  setVisibleCheckboxes?: (values: string[]) => void;
  additionalFilters?: string;
  title?: React.JSX.Element;
}
export default function Glossary({
  appDisplay,
  tableCheckBoxes,
  setVisibleCheckboxes,
  additionalFilters,
  title,
}: Props) {
  const [metaData, setMetaData] = React.useState<Metadata>({
    count: 0,
  });

  const [glossaryData, setGlossaryData] = React.useState<SimpleTableRow[]>([]);

  const siteUsageHeaderMetaData: Headers[] = [
    { title: "", apiField: "", colSpan: 3 },
    { title: "Variable Site Usage", apiField: "APPLICATIONS", colSpan: 7 },
  ];

  const siteNameSyle = {
    textAlign: "center",
    backgroundColor: "#194481",
    color: "white",
  };

  let headerMetaData: Headers[] = [
    {
      title: "Variable",
      apiField: "VARIABLE",
      style: {
        textAlign: "left",
        backgroundColor: "#194481",
        color: "white",
        zIndex: 3,
      },
    },
    {
      title: "Topic/Title",
      apiField: "LABEL",
      style: {
        textAlign: "left",
        backgroundColor: "#194481",
        color: "white",
      },
    },
    {
      title: "Definition",
      apiField: "DEFINITION",
    },
    {
      title: "FIB",
      apiField: "FIB",
      style: siteNameSyle,
    },
    {
      title: "BSC",
      apiField: "BSC",
      style: siteNameSyle,
    },
    {
      title: "SOD",
      apiField: "SOD",
      style: siteNameSyle,
    },
    {
      title: "R&C",
      apiField: "R&C",
      style: siteNameSyle,
    },

    { title: "ADS", apiField: "ADS", style: siteNameSyle },
    { title: "F&A", apiField: "F&A", style: siteNameSyle },
  ];
  const [tabView, setTabView] = React.useState("variable");

  const transformDataToTableBodyData = (tableData: KeyValue[]) => {
    return tableData.map((data: KeyValue, index) => {
      const tableCellData: SimpleTableRow = {};
      const widths = ["10%", "10%", "50%"];

      headerMetaData
        .map((value: Headers) => value.apiField)
        .forEach((apiField: string, i) => {
          const backgroundColor = index % 2 === 0 ? "#e9edf5" : "white";
          tableCellData[apiField] = {
            value: data[apiField],
            styles: {
              textAlign: "left",
              backgroundColor,
              zIndex: 1,
              width: widths[i],
            },
          };
        });
      const thisSites = data.APPLICATIONS.map((curr: any) => curr.SHORT_NAME);
      sites.forEach((site) => {
        const backgroundColor = index % 2 === 0 ? "#d9e3ef" : "#ecf3fa";
        tableCellData[site.shortName] = thisSites.includes(site.shortName)
          ? {
              value: "•",
              styles: {
                backgroundColor: "#cce8e8",
                fontSize: 26,
                width: "1%",
              },
            }
          : {
              value: "",
              styles: {
                backgroundColor: backgroundColor,
                width: "1%",
              },
            };
      });

      return tableCellData;
    });
  };
  const {
    page,
    rowsPerPage,
    handlePageJump,
    handleChangePage,
    handleChangeRowsPerPage,
  } = usePagination(1, 25, metaData.count);

  const tableHeaders: TableHeader[][] = [
    siteUsageHeaderMetaData.map((element) => ({
      title: element.title,
      styles: {
        textAlign: "center",
        fontWeight: "bold",
        borderBottom:
          element.title === "Variable Site Usage" ? "1px solid white" : "",
      },
      colSpan: element.colSpan || 1,
      apiField: element.apiField,
    })),
    headerMetaData.map((element) => ({
      title: element.title,
      styles: element.style || {
        textAlign: "left",
        backgroundColor: "#194481",
        color: "white",
      },
      colSpan: element.colSpan || 1,
      apiField: element.apiField,
    })),
  ];

  const tableBody = (
    <SimpleTableBody
      tableCheckbox={tableCheckBoxes && tableCheckBoxes.body}
      tableData={transformDataToTableBodyData(glossaryData)}
    />
  );
  const initialVariableCheckboxList = reduceObj(
    alphabet.map((val: string) => {
      return { key: val, value: true };
    }),
  );
  const initialSiteCheckboxList = reduceObj(
    sites.map((val: KeyValue) => {
      return { key: val.id, value: true };
    }),
  );
  const [siteSelection, setSiteSelection] = React.useState<KeyValue>(
    initialSiteCheckboxList,
  );
  const [variableSelection, setVariableSelection] = React.useState<KeyValue>(
    initialVariableCheckboxList,
  );
  const [searchText, setSearchText] = React.useState("");

  const [selectedLetters, setSelectedLetters] = React.useState("");
  const [selectedSites, setSelectedSites] = React.useState("");
  const [downloadData, setDownloadData] = React.useState<[]>([]);
  const [includesAlpha, setIncludesAlpha] = React.useState<string[]>([]);
  const [cleanVariableName, setCleanVariableNames] = React.useState<string[]>(
    [],
  );
  const [variableName, setVariableName] = React.useState<string>("");
  const [variableNameInput, setVariableNameInput] = React.useState<string>("");

  const [topicInput, setTopicInput] = React.useState<string>("");
  const [topic, setTopic] = React.useState<string>("");

  const [resetTable, setResetTable] = React.useState(false);
  const [errorPills, setErrorPills] = React.useState<string[]>([]);
  const [textSearch, setTextSearch] = React.useState({
    definitionSearch: "",
  });
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);

  const handleTabSwitch = () => {
    tabView === "variable"
      ? onClickFilterByTopic(topicInput)
      : onClickFilterByName();
    setTabView((prev) => (prev === "variable" ? "topic" : "variable"));
  };

  React.useEffect(() => {
    if (includesAlpha.length < 1) {
      let isMounted = true;
      //additional filters for api call
      const otherFilters = additionalFilters ? ` AND ${additionalFilters}` : "";
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/definitions?filters=!VARIABLE:""${otherFilters}&agg_by=VARIABLE&agg_limit=10000`,
        )
          .then((response) => {
            if (isMounted) {
              const letters = new Set(
                response.data.map((val: KeyValue) => val.VARIABLE.charAt(0)),
              ) as unknown as string[];
              setIncludesAlpha([...letters, VtoZ]);
            }
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  });

  const addSitesToDownloadData = (data: any) => {
    return [...data].reduce((acc, curr) => {
      const sites = curr.APPLICATIONS.map((app: any) => app.SHORT_NAME);
      siteShortNames.forEach((site: string) => {
        if (sites.includes(site)) {
          curr[site] = true;
        } else {
          curr[site] = false;
        }
      });
      return [...acc, curr];
    }, []);
  };

  const onClickResetTable = React.useCallback(() => {
    const search = searchText ? `&search=LABEL:${searchText}` : "";
    const otherFilters = additionalFilters ? `AND ${additionalFilters}` : "";
    (async () => {
      await fetchData(
        `${ApiBaseUrl()}/definitions?filters=!VARIABLE:"" ${otherFilters} AND VARIABLE:(${selectedLetters}*) AND APPLICATIONS.ID:(${
          appDisplay
            ? appDisplay
            : selectedSites
              ? selectedSites
              : sites.map((site) => site.id).join(" OR ")
        })${search}&limit=${rowsPerPage}&offset=${
          (page - 1) * rowsPerPage
        }&sort_by=VARIABLE&sort_order=ASC`,
      )
        .then((response) => {
          setVisibleCheckboxes &&
            setVisibleCheckboxes(
              response.data.map((res: KeyValue) => res.VARIABLE),
            );
          setGlossaryData(response.data);
          setMetaData({ count: response.totals.count });
          setResetTable(false);
          setVariableName("");
        })
        .catch((error) => {
          console.error(`${error.name} : ${error.message}`);
        });
    })();
    (async () => {
      await fetchData(
        `${ApiBaseUrl()}/definitions?filters=!VARIABLE:"" AND VARIABLE:(${selectedLetters}*) AND APPLICATIONS.ID:(${
          selectedSites
            ? selectedSites
            : sites.map((site) => site.id).join(" OR ")
        })&limit=3000&sort_by=VARIABLE&sort_order=ASC`,
      )
        .then((response) => {
          const newDownloadData = addSitesToDownloadData(response.data);
          setDownloadData(newDownloadData);
        })
        .catch((error) => {
          console.error(`${error.name} : ${error.message}`);
        });
    })();
  }, [
    additionalFilters,
    appDisplay,
    page,
    rowsPerPage,
    searchText,
    selectedLetters,
    selectedSites,
    setVisibleCheckboxes,
  ]);

  const onClickFilterByName = React.useCallback(
    (newSelectedSites?: string | null, newSelectedLetters?: string) => {
      setVariableName(variableNameInput);
      const selectedVars = variableNameInput
        .replace(/[ \n]/g, "")
        .split(",")
        .filter((variable: string) => variable !== "");
      const variableStr = newSelectedLetters
        ? `${newSelectedLetters}*`
        : selectedVars.join(" OR ");

      const variableSearch = variableStr
        ? `AND VARIABLE:(${variableStr}) `
        : selectedLetters
          ? `AND VARIABLE:(${selectedLetters}*) `
          : "";

      const search = searchText ? `&search=LABEL:${searchText}` : "";

      const selectedSitesStr = newSelectedSites
        ? newSelectedSites
        : selectedSites
          ? selectedSites
          : sites.map((site: any) => site.id).join(" OR ");
      const otherFilters = additionalFilters ? `AND ${additionalFilters}` : "";

      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/definitions?filters=APPLICATIONS.ID:(${selectedSitesStr}) AND !VARIABLE:"" ${variableSearch} ${otherFilters}&limit=${rowsPerPage}&offset=${
            (page - 1) * rowsPerPage
          }${search}&sort_by=VARIABLE&sort_order=ASC`,
        )
          .then((response) => {
            const responseVars = response.data.map((e: any) => e.VARIABLE);
            setCleanVariableNames(responseVars);
            !initialLoad && setResetTable(true);
            setVisibleCheckboxes &&
              setVisibleCheckboxes(
                response.data.map((res: KeyValue) => res.VARIABLE),
              );
            setGlossaryData(response.data);

            setMetaData({ count: response.totals.count });
          })
          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/definitions?filters=APPLICATIONS.ID:(${selectedSitesStr}) AND !VARIABLE:"" ${variableSearch}&limit=5000`,
        )
          .then((response) => {
            const newDownloadData = addSitesToDownloadData(response.data);
            setDownloadData(newDownloadData);
            const allResponseVars = response.data.map((e: any) => e.VARIABLE);
            setCleanVariableNames(allResponseVars);
            if (selectedVars.length > 0) {
              setErrorPills(
                selectedVars.filter((v) => !allResponseVars.includes(v)),
              );
            } else {
              setErrorPills([]);
            }
          })

          .catch((error) => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
    },
    [
      additionalFilters,
      initialLoad,
      page,
      rowsPerPage,
      searchText,
      selectedLetters,
      selectedSites,
      setVisibleCheckboxes,
      variableNameInput,
    ],
  );

  const onClickFilterByTopic = React.useCallback(
    (topicInput: string, selecedSites?: string, reset?: boolean) => {
      !reset && setTopic(topicInput);

      const selectedSitesStr = selecedSites
        ? selecedSites
        : selectedSites
          ? selectedSites
          : sites.map((site: any) => site.id).join(" OR ");

      if (topicInput === "" || reset) {
        (async () => {
          await fetchData(
            `${ApiBaseUrl()}/definitions?filters=APPLICATIONS.ID:(${selectedSitesStr})&limit=${rowsPerPage}&offset=${
              (page - 1) * rowsPerPage
            }&sort_by=VARIABLE&sort_order=ASC`,
          ).then((response) => {
            setResetTable(true);
            setGlossaryData(response.data);
            setMetaData({ count: response.totals.count });
          });
          await fetchData(
            `${ApiBaseUrl()}/definitions?filters=APPLICATIONS.ID:(${selectedSitesStr})&limit=3000&sort_by=VARIABLE&sort_order=ASC`,
          ).then((response) => {
            const newDownloadData = addSitesToDownloadData(response.data);
            setDownloadData(newDownloadData);
          });
        })();
      } else {
        const urls = [
          `filters=APPLICATIONS.ID:(${selectedSitesStr})&search=LABEL:${topicInput}`,
          `filters=APPLICATIONS.ID:(${selectedSitesStr})&search=DEFINITION:${topicInput}`,
        ];

        (async () => {
          await Promise.all(
            urls.map((url) =>
              fetchData(`${ApiBaseUrl()}/definitions?${url}&limit=3000`),
            ),
          )
            .then((response) => {
              const flatArr = response.map((res) => res.data).flat();
              const deduplicated = flatArr
                .filter(
                  (item, index, self) =>
                    index === self.findIndex((other) => isEqual(item, other)),
                )
                .sort((a, b) => a.VARIABLE.localeCompare(b.VARIABLE));

              const newDownloadData = addSitesToDownloadData(deduplicated);
              setDownloadData(newDownloadData);

              const paginated = deduplicated.slice(
                (page - 1) * rowsPerPage,
                page * rowsPerPage,
              );
              return {
                data: paginated,
                totals: { count: deduplicated.length },
              };
            })
            .then((response) => {
              setResetTable(true);
              setGlossaryData(response.data);
              setMetaData({ count: response.totals.count });
              setErrorPills(response.totals.count === 0 ? [topicInput] : []);
            });
        })();
      }
    },
    [page, rowsPerPage, selectedSites],
  );

  React.useEffect(() => {
    if (initialLoad) {
      onClickFilterByName();
      setInitialLoad(false);
    }
  }, [initialLoad, onClickFilterByName]);

  //handles pagination updates
  React.useEffect(() => {
    if (currentPage !== page || rowsPerPage !== glossaryData.length) {
      if (tabView === "topic") {
        onClickFilterByTopic(topicInput);
      } else if (tabView === "variable") {
        onClickFilterByName();
      }
      setCurrentPage(page);
    }
  }, [
    initialLoad,
    onClickFilterByTopic,
    page,
    tabView,
    rowsPerPage,
    currentPage,
    onClickFilterByName,
    glossaryData.length,
    topicInput,
  ]);

  const header = [
    { label: "Variable", key: "VARIABLE" },
    { label: "Title", key: "LABEL" },
    { label: "Definition", key: "DEFINITION" },
    { label: "FIB", key: "FIB" },
    { label: "BSC", key: "BSC" },
    { label: "SOD", key: "SOD" },
    { label: "R&C", key: "R&C" },
    { label: "ADS", key: "ADS" },
    { label: "F&A", key: "F&A" },
  ];

  const selectAllSitesClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checkboxState: KeyValue = reduceObj(
      Object.keys(siteSelection).map((val: string) => {
        return { key: val, value: e.target.checked };
      }),
    );
    setSiteSelection(checkboxState);
    const newSelectedSites = Object.values(checkboxState).every(
      (value) => value === false,
    )
      ? "0"
      : sites.map((site) => site.id).join(" OR ");
    setSelectedSites(newSelectedSites);
    setResetTable(false);
    if (tabView === "topic") {
      onClickFilterByTopic(newSelectedSites);
    } else {
      onClickFilterByName(newSelectedSites);
    }
  };
  const selectAllVariablesClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checkboxState: KeyValue = reduceObj(
      Object.keys(variableSelection).map((val: string) => {
        return { key: val, value: e.target.checked };
      }),
    );
    setVariableSelection(checkboxState);
    const selectedVals = Object.keys(checkboxState).filter((value: string) => {
      return checkboxState[value];
    });
    const newSelectedLetters = selectedVals.length > 0 ? "" : "NONE";
    setSelectedLetters(newSelectedLetters);
    setResetTable(false);
    onClickFilterByName(null, newSelectedLetters);
  };
  const selectAllVariables = (
    <Checkbox
      value={"ALL-Variables"}
      id={"checkbox-ALL-Variables"}
      checked={Object.values(variableSelection).every(
        (item: boolean) => item === true,
      )}
      onChange={selectAllVariablesClick}
      icon={<Box className={`${classes.icon} all`}>Select All</Box>}
      checkedIcon={
        <Box className={`${classes.icon} checked all`}>Deselect All</Box>
      }
    />
  );
  const selectAllSites = (
    <Grid
      size={{
        xs: 12,
        lg: 6,
      }}
    >
      <FormControlLabel
        id="checkbox-ALL-Sites"
        control={
          <Checkbox
            color="primary"
            value="ALL-Sites"
            id="check-ALL"
            checked={Object.values(siteSelection).every(
              (item: boolean) => item === true,
            )}
            onChange={selectAllSitesClick}
          />
        }
        sx={{
          "margin": 0,
          "minWidth": 235,
          "& span": {
            color: (theme) => theme.palette.primary.main,
          },
        }}
        label={
          <span>
            <b>Select All/None</b>
          </span>
        }
      />
    </Grid>
  );
  const onVariableClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelections: KeyValue = {
      ...variableSelection,
      [e.target.value]: e.target.checked,
    };
    setVariableSelection(newSelections);

    const selectedVals = Object.keys(newSelections).filter((value: string) => {
      return newSelections[value];
    });
    const newSelectedVals =
      selectedVals.length > 0 ? selectedVals.join("* OR ") : "NONE";
    setSelectedLetters(newSelectedVals);
    setResetTable(false);
    onClickFilterByName(null, newSelectedVals);
  };
  const onSiteClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSelections: KeyValue = {
      ...siteSelection,
      [e.target.value]: e.target.checked,
    };
    setSiteSelection(newSelections);

    const selectedVals = Object.keys(newSelections).filter((value: string) => {
      return newSelections[value];
    });
    setSelectedSites(selectedVals.length > 0 ? selectedVals.join(" OR ") : "0");
    setResetTable(false);
    const newSelectedSites =
      selectedVals.length > 0 ? selectedVals.join(" OR ") : "0";
    if (tabView === "topic") {
      onClickFilterByTopic(newSelectedSites);
    } else {
      onClickFilterByName(newSelectedSites);
    }
  };

  const tabNames = ["View by Variable Name", "View by Topic"];
  const errorPill = errorPills.map((e: string) => {
    return { value: e, search: false, formKey: "errorVariable" };
  });

  const filterByLetter = (
    <>
      <Typography variant="h4">Filter Variables by Letter</Typography>
      <Grid container spacing={2}>
        <Grid style={{ padding: ".5rem" }} size={12}>
          {selectAllVariables}
          {alphabet.map((e, index) => {
            return (
              <Checkbox
                key={index}
                id={`checkbox-${e === VtoZ ? "V-Z" : e}`}
                value={e}
                disabled={!includesAlpha.includes(e)}
                checked={includesAlpha.includes(e) && variableSelection[e]}
                onChange={onVariableClick}
                sx={{
                  padding: "6px",
                }}
                icon={
                  <Box
                    id={`checkedStatus-${e}`}
                    className={`${classes.icon} ${
                      !includesAlpha.includes(e) && "disabled"
                    }`}
                  >
                    {e.includes("V") ? "V-Z" : e}
                  </Box>
                }
                checkedIcon={
                  <Box
                    id={`checkedStatus-${e}`}
                    className={`${
                      classes.icon
                    } checked ${!includesAlpha.includes(e) && "disabled"}`}
                  >
                    {e.includes("V") ? "V-Z" : e}
                  </Box>
                }
              />
            );
          })}
        </Grid>
      </Grid>
    </>
  );

  const tabPanels = [
    <>
      {filterByLetter}
      <Grid container spacing={2}>
        <Grid size={6}>
          <Typography variant="h4">Filter Variables by Name</Typography>
          <TextField
            id="name-filter-input"
            placeholder="Enter many, comma separated variable names to filter."
            variant="outlined"
            multiline
            minRows={5}
            fullWidth
            value={variableNameInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (
                /^[a-zA-Z0-9_, ]+$/.test(event.target.value) ||
                event.target.value === ""
              ) {
                setVariableNameInput(event.target.value.toLocaleUpperCase());
                setResetTable(false);
              }
            }}
          />
        </Grid>
        <Grid size={6}>
          <Typography variant="h4" style={{ paddingBottom: 4 }}>
            Filter Variables by Site Usage
          </Typography>
          <Grid container> {selectAllSites}</Grid>
          <Grid container>
            {sites
              .filter((e) => e.id !== 0) //remove filter to activate ADS and F&A
              .map((e, index) => {
                return (
                  <Grid
                    key={index}
                    size={{
                      xs: 12,
                      lg: 6,
                    }}
                  >
                    <FormControlLabel
                      key={index}
                      id={`checkbox-${e.shortName}`}
                      control={
                        <Checkbox
                          color="primary"
                          id={`checkbox-${e.shortName}-${e.id}`}
                          value={e.id}
                          checked={siteSelection[e.id]}
                          onChange={onSiteClick}
                        />
                      }
                      sx={{
                        "margin": 0,
                        "minWidth": 235,
                        "& span": {
                          color: (theme) => theme.palette.primary.main,
                        },
                      }}
                      label={
                        <span>
                          <b>{e.shortName}</b> - {e.longName}
                        </span>
                      }
                    />
                  </Grid>
                );
              })}
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={1}></Grid>
        <Grid size={4}>
          <Button
            id="filter-by-name-button"
            variant="outlined"
            color="primary"
            disabled={
              variableName.length === 0 && variableNameInput.length === 0
            }
            className={clsx(classes.button, "filterTableButton")}
            onClick={() => {
              if (
                resetTable ||
                (variableName.length !== 0 && variableNameInput.length === 0)
              ) {
                setVariableName("");
                setVariableNameInput("");
                setErrorPills([]);
                onClickResetTable();
              } else {
                onClickFilterByName();
              }
            }}
          >
            {resetTable ? "Reset Table" : "Filter Table Display"}
          </Button>
        </Grid>
        <Grid size={2}></Grid>
        <Grid size={4}>
          <CSVLink
            style={{ textAlign: "center" }}
            headers={header}
            className={clsx(classes.button, "CSVLink")}
            filename={`${fileNameFormat("Definitions")}.csv`}
            data={downloadData}
            asyncOnClick={true}
          >
            Download Table as Shown
          </CSVLink>
        </Grid>
        <Grid size={1}></Grid>
      </Grid>
    </>,
    <>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Typography variant="h4">Filter Table by Topic</Typography>
          <TextField
            id="topic-filter-input"
            placeholder="Use words and phrases to reveal related variables. System will search variable  titles, descriptions, and definition to produce matching results."
            variant="outlined"
            multiline
            minRows={5}
            fullWidth
            value={topicInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setTopicInput(event.target.value);
              setResetTable(false);
            }}
          />
        </Grid>
        <Grid size={6}>
          <Typography variant="h4" style={{ paddingBottom: 4 }}>
            Filter Variables by Site Usage
          </Typography>
          <Grid container> {selectAllSites}</Grid>
          <Grid container>
            {sites
              .filter((e) => e.id !== 0) //remove filter to activate ADS and F&A
              .map((e, index) => {
                return (
                  <Grid
                    key={index}
                    size={{
                      xs: 12,
                      lg: 6,
                    }}
                  >
                    <FormControlLabel
                      key={index}
                      id={`checkbox-${e.shortName}`}
                      control={
                        <Checkbox
                          color="primary"
                          id={`checkbox-${e.shortName}-${e.id}`}
                          value={e.id}
                          checked={siteSelection[e.id]}
                          onChange={onSiteClick}
                        />
                      }
                      sx={{
                        "margin": 0,
                        "minWidth": 235,
                        "& span": {
                          color: (theme) => theme.palette.primary.main,
                        },
                      }}
                      label={
                        <span>
                          <b>{e.shortName}</b> - {e.longName}
                        </span>
                      }
                    />
                  </Grid>
                );
              })}
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={1}></Grid>
        <Grid size={4}>
          <Button
            id="filter-by-topic-button"
            variant="outlined"
            color="primary"
            disabled={topicInput.length === 0 && topic.length === 0}
            className={clsx(classes.button, "filterTableButton")}
            onClick={() => {
              if (resetTable) {
                setTopicInput("");
                setTopic("");
                handlePageJump({ pageNumber: 1 });
              }
              onClickFilterByTopic(topicInput, selectedSites, resetTable);
            }}
          >
            {resetTable ? "Reset Table" : "Filter Table Display"}
          </Button>
        </Grid>
        <Grid size={2}></Grid>
        <Grid size={4}>
          <CSVLink
            style={{ textAlign: "center" }}
            headers={header}
            className={clsx(classes.button, "CSVLink")}
            filename={`${fileNameFormat("Definitions")}.csv`}
            data={downloadData}
            asyncOnClick={true}
          >
            Download Table as Shown
          </CSVLink>
        </Grid>
        <Grid size={1}></Grid>
      </Grid>
    </>,
  ];
  const definitionFilter = (
    <form
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault();
        setSearchText(textSearch.definitionSearch);
        onClickFilterByTopic(textSearch.definitionSearch);
      }}
      style={{ paddingBottom: "1rem" }}
    >
      <FormControl variant="outlined" fullWidth size="small" margin="dense">
        <TextFilterField
          currentValue={textSearch.definitionSearch}
          fieldName={"definitionSearch"}
          label={"Search by Title"}
          form={textSearch}
          setForm={setTextSearch}
          endIcon={
            <Button
              type="submit"
              title="search"
              style={{ marginTop: 0, marginRight: -14, minWidth: 0 }}
            >
              <SearchIcon />
            </Button>
          }
        />
      </FormControl>
    </form>
  );
  const table = (
    <div>
      <Typography component="div" id="multi-header-table-title">
        <Typography variant="h3" id="glossaryCount">
          Displaying {metaData.count.toLocaleString()} Variables
        </Typography>
        <Divider style={{ marginBottom: "1rem" }} />
      </Typography>
      <ResultPagination
        count={metaData.count}
        handleChangePage={handleChangePage as any}
        handlePickPage={handlePageJump}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        limit={rowsPerPage}
        pageNumber={page}
      >
        <Box
          sx={{
            overflow: "scroll",
          }}
        >
          <Typography variant="h3" style={{ color: "#660000" }}>
            {errorPills.length > 0
              ? "The following entries were not recognized:"
              : ""}
          </Typography>

          <SearchPills
            dataArray={errorPill}
            errorColor
            deleteSearchPill={(e) => {
              if (tabView === "topic") {
                let newTopicStr = topic.replace(e.value, "");
                setTopic(newTopicStr);
                setTopicInput(newTopicStr);
                setErrorPills(
                  errorPills.filter((element) => element !== e.value),
                );
                return onClickResetTable();
              } else {
                const updatedVariableNames = cleanVariableName
                  .concat(errorPills.filter((element) => element !== e.value))
                  .filter((value: string) => value !== "")
                  .join(", ");
                setErrorPills(
                  errorPills.filter((element) => element !== e.value),
                );
                if (updatedVariableNames.length === 0) {
                  return onClickResetTable();
                }
                setVariableNameInput(updatedVariableNames);
              }
            }}
          />
          <MultiHeaderTable
            headerCheckbox={tableCheckBoxes && tableCheckBoxes.header}
            tableBody={tableBody}
            tableHeaders={tableHeaders}
            useStickyHeader
          />
        </Box>
      </ResultPagination>
    </div>
  );

  return appDisplay ? (
    <Root>
      {filterByLetter}
      {definitionFilter}
      {table}
    </Root>
  ) : (
    <>
      <Grid sx={{ padding: 1 }} size={9}>
        <Root>
          {title}
          <Typography variant="body2" color="primary.main">
            Use the table below to look-up variables by name to see their
            descriptions and definitions. You can filter your results by
            variable name and by BankFind Suite usage, or use the View by Topic
            tab to see how topics are related to variables through out our site.
          </Typography>
          <BasicTabs
            TabNames={tabNames}
            TabContent={tabPanels}
            onChange={handleTabSwitch}
          />
        </Root>
      </Grid>
      <Grid sx={{ padding: 2 }} size={12}>
        <Root>{table}</Root>
      </Grid>
    </>
  );
}
